import { z } from "zod";
import { CONTRACT_TYPES, PROPERTY_TYPES } from "./portal.js";

// ============================================================================
// Bulk listing import — canonical importable field set + parsing/validation.
// Shared so the admin (live preview) and the worker (authoritative commit) run
// the EXACT same coercion + validation. v1 imports TEXT/DATA only (no photos —
// those stay on the per-listing media pipeline).
// ============================================================================

export type ImportFieldType = "string" | "i18n" | "number" | "list" | "contract" | "property_type" | "coord" | "province";

export type ImportField = {
  /** canonical key used in the row object sent to the worker */
  key: string;
  /** human label shown in the mapping UI */
  label: string;
  type: ImportFieldType;
  /** lands as a listings-row column (vs a content_versions.payload field) */
  column?: boolean;
  /** header aliases (lowercased, accent-stripped) for auto-mapping */
  aliases: string[];
};

/** The fields an agency CSV can map onto. Order = display order in the UI. */
export const IMPORT_FIELDS: readonly ImportField[] = [
  { key: "slug", label: "Slug (URL)", type: "string", column: true, aliases: ["slug", "url", "riferimento", "ref", "reference", "codice", "id"] },
  { key: "title", label: "Titolo", type: "i18n", aliases: ["title", "titolo", "nome", "name", "oggetto"] },
  { key: "description", label: "Descrizione", type: "i18n", aliases: ["description", "descrizione", "testo", "dettaglio", "note", "annuncio"] },
  { key: "contract", label: "Contratto", type: "contract", column: true, aliases: ["contract", "contratto", "transazione", "transaction", "tipo annuncio", "vendita affitto"] },
  { key: "property_type", label: "Tipologia", type: "property_type", column: true, aliases: ["property type", "tipologia", "tipo", "type", "categoria", "category"] },
  { key: "prezzo", label: "Prezzo", type: "number", aliases: ["prezzo", "price", "costo", "importo", "valore", "euro"] },
  { key: "mq", label: "Superficie (mq)", type: "number", aliases: ["mq", "superficie", "surface", "metri", "sqm", "size", "mq commerciali"] },
  { key: "rooms", label: "Locali / vani", type: "number", aliases: ["rooms", "locali", "vani", "stanze", "camere", "bedrooms", "n locali"] },
  { key: "bathrooms", label: "Bagni", type: "number", aliases: ["bathrooms", "bagni", "baths", "wc", "servizi"] },
  { key: "piano", label: "Piano", type: "string", aliases: ["piano", "floor", "livello"] },
  { key: "ape", label: "Classe energetica (APE)", type: "string", aliases: ["ape", "classe energetica", "energy class", "energia", "epc", "classe"] },
  { key: "ipe", label: "IPE (kWh/m²)", type: "number", aliases: ["ipe", "indice prestazione", "kwh", "consumo"] },
  { key: "city", label: "Città / Comune", type: "string", column: true, aliases: ["city", "citta", "comune", "town", "localita", "paese"] },
  { key: "zone", label: "Zona / Frazione", type: "string", column: true, aliases: ["zone", "zona", "frazione", "quartiere", "area", "district"] },
  { key: "province", label: "Provincia (sigla)", type: "province", column: true, aliases: ["province", "provincia", "prov", "pr"] },
  { key: "region", label: "Regione", type: "string", column: true, aliases: ["region", "regione"] },
  { key: "lat", label: "Latitudine", type: "coord", column: true, aliases: ["lat", "latitude", "latitudine"] },
  { key: "lng", label: "Longitudine", type: "coord", column: true, aliases: ["lng", "lon", "long", "longitude", "longitudine"] },
  { key: "features", label: "Caratteristiche", type: "list", aliases: ["features", "caratteristiche", "dotazioni", "amenities", "extra", "optional"] },
] as const;

export const IMPORT_FIELD_KEYS = IMPORT_FIELDS.map((f) => f.key);

/** Strip diacritics, lowercase, collapse to kebab-case ASCII. */
export function slugify(input: string): string {
  return input
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 110);
}

/** Normalize a header cell for alias matching. */
function normHeader(h: string): string {
  return h.normalize("NFD").replace(new RegExp("[\\u0300-\\u036f]", "g"), "").toLowerCase().trim().replace(/[_\-]+/g, " ").replace(/\s+/g, " ");
}

/**
 * Suggest a mapping from import-field-key -> source column index (or null).
 * Exact alias match wins over substring; first unused column claimed.
 */
export function suggestMapping(columns: string[]): Record<string, number | null> {
  const norm = columns.map(normHeader);
  const used = new Set<number>();
  const out: Record<string, number | null> = {};
  for (const field of IMPORT_FIELDS) {
    let found: number | null = null;
    // pass 1: exact
    for (let i = 0; i < norm.length; i++) {
      if (used.has(i)) continue;
      if (field.aliases.includes(norm[i]!)) { found = i; break; }
    }
    // pass 2: substring — only for aliases >= 4 chars and only header-contains-alias
    // (the reverse direction + tiny aliases like "pr"/"id" caused greedy mis-grabs).
    if (found === null) {
      for (let i = 0; i < norm.length; i++) {
        if (used.has(i)) continue;
        if (field.aliases.some((a) => a.length >= 4 && norm[i]!.includes(a))) { found = i; break; }
      }
    }
    if (found !== null) used.add(found);
    out[field.key] = found;
  }
  return out;
}

/** Parse an Italian/European-formatted number string. Returns null if empty/NaN. */
export function parseItNumber(raw: string): number | null {
  if (/[0-9][eE][+-]?[0-9]/.test(raw)) return null; // reject scientific notation (would mis-parse "1e3" -> 13)
  let s = raw.replace(/[^0-9.,-]/g, "").trim();
  if (!s) return null;
  const hasDot = s.includes(".");
  const hasComma = s.includes(",");
  if (hasDot && hasComma) {
    // last separator is the decimal one
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) s = s.replace(/\./g, "").replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if (hasComma) {
    const dec = s.split(",").pop() ?? "";
    s = dec.length === 1 || dec.length === 2 ? s.replace(",", ".") : s.replace(/,/g, "");
  } else if (hasDot) {
    const dec = s.split(".").pop() ?? "";
    if (!(dec.length === 1 || dec.length === 2) || s.split(".").length > 2) s = s.replace(/\./g, "");
  }
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

const APE_CLASSES = new Set(["A4", "A3", "A2", "A1", "A+", "A", "B", "C", "D", "E", "F", "G"]);

const CONTRACT_ALIASES: Record<string, (typeof CONTRACT_TYPES)[number]> = {
  vendita: "vendita", sale: "vendita", vendesi: "vendita", buy: "vendita", acquisto: "vendita",
  affitto: "affitto", rent: "affitto", affittasi: "affitto", locazione: "affitto", rental: "affitto",
};
const PROPERTY_ALIASES: Record<string, (typeof PROPERTY_TYPES)[number]> = {
  appartamento: "appartamento", apartment: "appartamento", flat: "appartamento", trilocale: "appartamento", bilocale: "appartamento", quadrilocale: "appartamento", monolocale: "appartamento",
  attico: "attico", penthouse: "attico",
  villa: "villa", villetta: "villetta_a_schiera", "villetta a schiera": "villetta_a_schiera", schiera: "villetta_a_schiera", townhouse: "villetta_a_schiera",
  bifamiliare: "bifamiliare", "casa indipendente": "villa", indipendente: "villa",
  rustico: "rustico_casale", casale: "rustico_casale", cascina: "rustico_casale",
  terreno: "terreno", land: "terreno",
  "nuova costruzione": "nuova_costruzione", cantiere: "nuova_costruzione",
  mansarda: "mansarda", loft: "loft",
  commerciale: "locale_commerciale", negozio: "locale_commerciale", shop: "locale_commerciale",
  ufficio: "ufficio", office: "ufficio",
  box: "box_garage", garage: "box_garage", posto_auto: "box_garage",
};

/** Common full province names -> sigla (Garda area + major cities). Extend as needed. */
const PROVINCE_SIGLA: Record<string, string> = {
  verona: "VR", brescia: "BS", mantova: "MN", trento: "TN", "trento tn": "TN",
  milano: "MI", monza: "MB", bergamo: "BG", como: "CO", lecco: "LC", varese: "VA",
  venezia: "VE", padova: "PD", vicenza: "VI", treviso: "TV", rovigo: "RO", belluno: "BL",
  bologna: "BO", modena: "MO", roma: "RM", torino: "TO", firenze: "FI", napoli: "NA", bolzano: "BZ",
};

export type CoerceResult =
  | { ok: true; value: unknown; warning?: string }
  | { ok: false; error: string };

/** Coerce one raw cell for a given field. `value: undefined` means "leave empty". */
export function coerceImportValue(field: ImportField, raw: string): CoerceResult {
  const v = (raw ?? "").trim();
  if (!v) return { ok: true, value: undefined };
  switch (field.type) {
    case "string":
      return { ok: true, value: v.slice(0, 240) };
    case "i18n":
      return { ok: true, value: { it: v } };
    case "list": {
      const items = v.split(/[;,|]/).map((x) => x.trim()).filter(Boolean).slice(0, 40);
      return { ok: true, value: items };
    }
    case "number": {
      const n = parseItNumber(v);
      if (n === null) return { ok: false, error: `"${v}" non è un numero` };
      return { ok: true, value: n };
    }
    case "coord": {
      // Coordinates are always decimal (never thousand-separated) + range-checked.
      const n = Number.parseFloat(v.replace(/[^0-9.,+-]/g, "").replace(",", "."));
      if (!Number.isFinite(n)) return { ok: false, error: `"${v}" non è una coordinata` };
      const max = field.key === "lat" ? 90 : 180;
      if (Math.abs(n) > max) return { ok: true, value: undefined, warning: `${field.key} "${v}" fuori range` };
      return { ok: true, value: n };
    }
    case "province": {
      const up = v.toUpperCase();
      if (/^[A-Z]{2}$/.test(up)) return { ok: true, value: up };
      const sig = PROVINCE_SIGLA[normHeader(v)];
      if (sig) return { ok: true, value: sig };
      if (up.length <= 4) return { ok: true, value: up };
      return { ok: true, value: undefined, warning: `provincia "${v}": usa la sigla (es. VR)` };
    }
    case "contract": {
      const key = normHeader(v);
      const c = CONTRACT_ALIASES[key];
      if (!c) return { ok: false, error: `contratto "${v}" non riconosciuto` };
      return { ok: true, value: c };
    }
    case "property_type": {
      const key = normHeader(v);
      const direct = (PROPERTY_TYPES as readonly string[]).includes(key) ? (key as (typeof PROPERTY_TYPES)[number]) : undefined;
      const pt = direct ?? PROPERTY_ALIASES[key];
      if (!pt) return { ok: true, value: undefined, warning: `tipologia "${v}" non mappata` };
      return { ok: true, value: pt };
    }
    default:
      return { ok: true, value: v };
  }
}

/** Special handling for APE (string field but validated against known classes). */
export function normalizeApe(raw: string): { value?: string; warning?: string } {
  const cleaned = (raw ?? "").trim().toUpperCase().replace(/\s+/g, "");
  if (!cleaned) return {};
  if (APE_CLASSES.has(cleaned)) return { value: cleaned };
  // "Classe A2" / "APE: B" -> extract the standalone class token (no garbage stored).
  for (const t of raw.toUpperCase().split(/[^A-Z0-9+]+/)) {
    if (APE_CLASSES.has(t)) return { value: t };
  }
  return { warning: `classe APE "${raw}" non standard` };
}

// ---- wire schemas (worker commit) ------------------------------------------

/** One row = a map of import-field-key -> raw string cell. */
export const ImportRow = z.record(z.enum(IMPORT_FIELD_KEYS as [string, ...string[]]), z.string());
export type ImportRow = z.infer<typeof ImportRow>;

export const ImportCommitInput = z.object({
  rows: z.array(ImportRow).min(1).max(200),
  defaults: z.object({
    contract: z.enum(CONTRACT_TYPES).default("vendita"),
    kind: z.enum(["immobile", "cantiere"]).default("immobile"),
  }),
});
export type ImportCommitInput = z.infer<typeof ImportCommitInput>;

export const ImportRowResult = z.object({
  row: z.number(),
  slug: z.string().nullable(),
  title: z.string().nullable(),
  status: z.enum(["created", "skipped", "error"]),
  message: z.string().optional(),
});
export type ImportRowResult = z.infer<typeof ImportRowResult>;

export const ImportCommitResponse = z.object({
  created: z.number(),
  skipped: z.number(),
  errors: z.number(),
  results: z.array(ImportRowResult),
});
export type ImportCommitResponse = z.infer<typeof ImportCommitResponse>;
